import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	GridView,
	Icon,
	Space,
	PipeSeparator,
	useContextMenu
} from "client/Components";
import {
	MediaQueryKind,
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ReactElement,
	RefObject,
	useMemo,
	useRef
} from "react";
import {
	GridItem as GridItemModel,
	RecycleBinAction,
	RecycleBinActionContext
} from "./SharePointRecycleBin";
import {
	GridViewGroupItem,
	GridViewItemKind
} from "client/Components/GridView/groupGridViewItems";
import { DocumentGridViewItem } from "../../DocumentGridViewItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil } from "client/Misc/utils";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { DocumentGridViewContainer } from "../../DocumentGridViewContainer";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { DocumentNoResult } from "../../DocumentNoResult";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type SharePointRecycleBinGridViewProps = {
	items: readonly GridItemModel[];
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: GridItemModel) => SelectableItemOptions;
	selectionActions: readonly RecycleBinAction[];
	actionContext: RecycleBinActionContext;
};

export const SharePointRecycleBinGridView = (props: SharePointRecycleBinGridViewProps): ReactElement => {
	const {
		items,
		multiSelection,
		selectionFor,
		selectionActions,
		actionContext
	} = props;

	const userTimezone: TimeZone = useUserTimezone();
	const gridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(gridRef);
	const isTouch: boolean = useIsTouchDevice();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const itemsToRender = useMemo(
		(): readonly GridViewGroupItem<GridItemModel>[] => {
			return items.map(
				x => ({
					model: x,
					uniqueKey: x.id,
					itemKind: x.model.itemType === DocumentItemType.Folder
						? GridViewItemKind.Default
						: GridViewItemKind.Preview
				})
			);
		},
		[items]
	);

	if (items.length === 0) {
		return (
			<DocumentNoResult
				hasSearch={false}
			/>
		);
	}

	return (
		<DocumentGridViewContainer>
			<GridView
				trackingId="share-point-recycle-bin-grid-view"
				ref={gridRef}
				items={itemsToRender}
				multiSelection={multiSelection}
				hasLassoSelection={!isMobile}
				elementToRenderAfterItems={
					items.length > 0
						? <AiAssistantFabSpacing />
						: undefined
				}
			>
				{ item => {
					const {
						itemKind,
						model,
						uniqueKey
					} = item;

					return (
						<DocumentGridViewItem
							key={uniqueKey}
							id={model.id}
							name={model.name}
							additionalInfo={(
								<Info
									deletedOn={model.deletedOn}
									size={model.size}
									timezone={userTimezone}
								/>
							)}
							selectable={selectionFor(model)}
							documentKind={model.model
								.itemType}
							isMobile={isMobile}
							selectionActions={selectionActions}
							isInSelectMode
							actionContext={{...actionContext, selection: [model.model]}}
							onContextMenu={contextMenuHandler}
							isTouch={isTouch}
							isPreview={itemKind === GridViewItemKind.Preview}
						/>
					);
				}}
			</GridView>
			{contextMenuProps && (
				<ContextMenu
					{...contextMenuProps}
					actions={selectionActions}
					context={actionContext}
					trackingId="sharepoint-recyclebin-context-menu"
					title={computeContextMenuTitle(
						actionContext.selection,
						(x) => x.name
					)}
				/>
			)}
		</DocumentGridViewContainer>
	);
};

SharePointRecycleBinGridView.displayName = "SharePointRecycleBinGridView";

type InfoProps = {
	deletedOn: Date;
	size: number | nil;
	timezone: TimeZone;
};

export const Info = (props: InfoProps): ReactElement => {
	const {
		deletedOn,
		size,
		timezone,
	} = props;

	return (
		<>
			<Icon value="trash-can" />
			<Space />
			<DateString
				format="RelativeTimestamp"
				timezone={timezone}
				value={deletedOn}
			/>
			{isNotNil(size) && (
				<>
					<PipeSeparator />
					{getDisplayFriendlySize(size)}
				</>
			)}
		</>
	);
};

Info.displayName = "Info";